import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TranslatableLabel } from '#src/app/shared/hooks/i18n';

const RESET_LABEL_TIMER = 3000;

type ShareData = {
	buttonLabel: TranslatableLabel;
	/** Whether the success label is showing. Drives the status region that announces it. */
	showSuccessLabel: boolean;
};
type ShareActions = { afterShare: (success: boolean) => void };
type UseDefaultShareButtonResult = ModelHookResult<ShareData, ShareActions>;
type UseShareButtonArgs = {
	label: TranslatableLabel;
	successLabel?: TranslatableLabel;
};

export function useRoundShareIconButton({ label, successLabel }: UseShareButtonArgs): UseDefaultShareButtonResult {
	// A counter rather than a flag, so that each success is a distinct value and restarts the reset below.
	const [successCount, setSuccessCount] = useState(0);

	// A false `success` means nothing was shared, e.g. a blocked clipboard write. Showing — and above
	// all announcing — a success the user did not get is worse than staying silent.
	const afterShare = useCallback(
		(success: boolean) => {
			if (successLabel && success) {
				setSuccessCount((count) => count + 1);
			}
		},
		[successLabel],
	);

	useEffect(() => {
		if (successCount === 0) return undefined;
		const timeout = window.setTimeout(() => setSuccessCount(0), RESET_LABEL_TIMER);
		return () => window.clearTimeout(timeout);
	}, [successCount]);

	const showSuccessLabel = successCount > 0;

	const buttonLabel = useMemo(
		() => (successLabel && showSuccessLabel ? successLabel : label),
		[showSuccessLabel, successLabel, label],
	);

	return {
		data: { buttonLabel, showSuccessLabel },
		actions: { afterShare },
	};
}
